import { FieldSelect, FieldSwitch, FormRowField, FormikForm, HeadingLabel } from "@skilitics/designer-ui-components";
import { useFormatMessage, type FormatMessages } from "@skilitics/localization";
import {
  useDarkMode,
  useEnableConsole,
  useFeatureSwitch,
  usePreviewAuth,
  useShowAiIndicators,
  type PreviewAuthSetting,
} from "@skilitics/nautilus-core-designer";

import { Section, StyledForm } from "./shared";

type FormValues = {
  darkMode: boolean;
  previewAuth: PreviewAuthSetting;
  enableConsole: boolean;
  showAiIndicators: boolean;
};

export const ApplicationPreferencesForm = () => {
  const formatMessage = useFormatMessage(formatStrings);
  const [darkMode, setDarkMode] = useDarkMode();
  const [previewAuth, setPreviewAuth] = usePreviewAuth();
  const [showAiIndicators, setShowAiIndicators] = useShowAiIndicators();
  const [enableConsole, setEnableConsole] = useEnableConsole();

  const previewAuthPrefAvailable = useFeatureSwitch("preview-auth-pref");
  const consoleLogging = useFeatureSwitch("console-logging");

  const initialValues = { darkMode, previewAuth, showAiIndicators, enableConsole };

  const onSubmit = (formValues: FormValues) => {
    setDarkMode(formValues.darkMode);
    setPreviewAuth(formValues.previewAuth);
    setShowAiIndicators(formValues.showAiIndicators);
    setEnableConsole(formValues.enableConsole);
  };

  return (
    <FormikForm<FormValues>
      initialValues={initialValues}
      onSubmit={onSubmit}
      submitOnBlur
      formatStrings={formatStrings}
      dataTest="application-preferences-form"
    >
      <StyledForm>
        <Section>
          <HeadingLabel>{formatMessage("appearance")}</HeadingLabel>
          <FormRowField<FormValues> name="darkMode">
            <FieldSwitch label={formatMessage("darkModeLabel")} />
          </FormRowField>
        </Section>

        <Section>
          <HeadingLabel>{formatMessage("aiHeading")}</HeadingLabel>

          <FormRowField<FormValues> name="showAiIndicators">
            <FieldSwitch hideDescriptionInTooltip />
          </FormRowField>
        </Section>

        {(previewAuthPrefAvailable || consoleLogging) && (
          <Section>
            <HeadingLabel>{formatMessage("previewHeading")}</HeadingLabel>
            {previewAuthPrefAvailable && (
              <FormRowField<FormValues> name="previewAuth">
                <FieldSelect
                  options={[
                    { value: "none", label: "Off" },
                    { value: "tenant", label: "On" },
                  ]}
                />
              </FormRowField>
            )}

            {consoleLogging && (
              <FormRowField<FormValues> name="enableConsole">
                <FieldSwitch label={formatMessage("enableConsoleLabel")} />
              </FormRowField>
            )}
          </Section>
        )}
      </StyledForm>
    </FormikForm>
  );
};

const formatStrings = {
  appearance: {
    en: `Appearance`,
  },
  aiHeading: {
    en: `AI Preferences`,
  },
  previewHeading: {
    en: `Content Preview Preferences`,
  },
  showAiIndicatorsLabel: {
    en: `AI Indicators`,
  },
  showAiIndicatorsDescription: {
    en: `Display an indicator next to content that has been generated by AI.`,
  },
  darkModeLabel: {
    en: `Dark Mode`,
  },
  previewAuthLabel: {
    en: "Authentication",
  },
  enableConsoleLabel: {
    en: `Console Logging`,
  },
} satisfies FormatMessages;
